<template>
  <div
    class="spotify-playlist-container"
    :style="{ width: width, height: height }"
  >

    <div
      v-if="!playerLoaded"
      class="spotify-facade"
      @click="loadPlayer"
      role="button"
      tabindex="0"
      :aria-label="`Load Spotify playlist: ${playlistName || 'Playlist'}`"
      @keydown.enter="loadPlayer"
      @keydown.space="loadPlayerPreventScroll"
    >
      <div class="facade-content">
        <img v-lazy="image">
        <hr>
        <p v-if="playlistName" class="playlist-name">{{ playlistName }}</p>
        <p class="load-prompt">{{ loadPromptText }}</p>


      </div>
    </div>

    <iframe
      :style="{ borderRadius: '12px', display: 'block' }"
      :src="iframeSrc"
      :width="width"
      :height="height"
      frameBorder="0"
      allowfullscreen=""
      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
      loading="lazy"
      :title="`Spotify Playlist Player: ${playlistName || 'Playlist'}`"
    ></iframe>
  </div>
</template>

<script>
const SPOTIFY_EMBED_BASE_URL = "https://open.spotify.com/embed/playlist/";

export default {
  name: 'SpotifyPlaylist',
  props: {
    listId: {
      type: String,
      required: true,
      validator: function (value) {
        return value.trim().length > 0;
      }
    },
    width: {
      type: String,
      default: '100%'
    },
    height: {
      type: String,
      default: '352'
    },
    playlistName: { // New prop for displaying on the facade
      type: String,
      default: ''
    },
    loadPromptText: { // New prop for customizing the load prompt
      type: String,
      default: 'Click to load Spotify playlist'
    },
    image: {
      type: String,
      default: 'https://imgix.cosmicjs.com/a1e3c280-0531-11f0-993b-3bd041905fff-IMG_2203.jpg',
    }
    // You could add a 'placeholderImageUrl' prop here too if needed
  },
  data() {
    return {
      playerLoaded: false
    };
  },
  computed: {
    iframeSrc() {
      if (!this.listId) {
        return "";
      }
      return `${SPOTIFY_EMBED_BASE_URL}${this.listId}?theme=0`;
    }
  },
  methods: {
    loadPlayer() {
      if (!this.listId) {
        console.warn("SpotifyPlaylist: Cannot load player, 'listId' prop is missing or empty.");
        return;
      }
      this.playerLoaded = true;
    },
    loadPlayerPreventScroll(event) {
      // Prevent page scroll when activating with spacebar
      event.preventDefault();
      this.loadPlayer();
    }
  },
  mounted() {
    if (!this.listId) {
      console.warn("SpotifyPlaylist component is missing the required 'listId' prop during mount.");
    }
  }
};
</script>

<style scoped>
.spotify-playlist-container {
  /* Ensures the container itself has a line-height that doesn't add extra space if iframe is display:block */
  line-height: 0;
  background-color: #f0f0f0; /* Light background for the container before load */
  display: inline-block; /* Or block, depending on your layout needs */
  position: relative; /* For potential absolute positioning of icons/text inside facade */
  border-radius: 12px; /* Match iframe border-radius */
  overflow: hidden; /* Ensure content stays within border-radius */
}

.spotify-facade {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: #282828; /* Dark background similar to Spotify */
  color: white;
  text-align: center;
  padding: 20px;
  box-sizing: border-box; /* Include padding in width/height */
  transition: background-color 0.2s ease;
}

.spotify-facade:hover,
.spotify-facade:focus {
  background-color: #383838; /* Slightly lighter on hover/focus */
}

. facade-content {
  /* Styles for content within the facade */
}

.spotify-icon {
  width: 60px; /* Adjust size as needed */
  height: 60px;
  margin-bottom: 15px;
  opacity: 0.8;
}

.playlist-name {
  font-size: 1.1em;
  font-weight: bold;
  margin-bottom: 8px;
}

.load-prompt {
  margin-top: 20px;
  font-size: 0.9em;
}

/* Ensures the iframe itself is responsive if width is a percentage and takes up container space */
.spotify-playlist-container iframe {
  max-width: 100%;
  vertical-align: middle; /* Another trick to prevent extra space sometimes */
}
</style>
